import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { deactivateModal } from '../../store/actions';
import { REPORT_API_ENDPOINTS } from '../../constants';
import { storage } from '../../config';

import './WorkforceReportsModal.css';
import { ViewerPrimaryColorUserStyle } from '../UserCustomization';
import svgInline from '../SvgInline/SvgInline';
import closeIcon from '../../shared/assets/icon-close.svg';

class WorkforceReportsModalComponent extends React.Component {
  constructor(props) {
    super(props);

    // Removed debug logging

    // Get property from either direct prop or properties array
    let property = props.property;
    console.log('PROPERTY = ', property);

    // If no direct property, try the properties array
    if (!property && props.properties && props.properties.length > 0) {
      property = props.properties[0];
    }

    // Default values
    let addr1 = property ? (property.name || '') : '';
    let addr2 = property ? this.getPropertyAddress(property) : '';

    // For area objects, format county/community with state code and clear subtitle
    if (property && property.areaType) {
      if (property.areaType === 'counties') {
        const stateCd = property.state_cd || property.state || '';
        addr1 = `${property.name} County, ${stateCd}`;
      } else if (property.areaType === 'communities') {
        const stateCd = property.state_cd || property.state || '';
        addr1 = `${property.name}, ${stateCd}`;
      } else {
        addr1 = property.name || '';
      }
      addr2 = '';
    }

    this.state = {
      driveType: 'mins',
      driveValues: [3, 5, 8],
      addressLine1: addr1,
      addressLine2: addr2,
      isGeneratingReport: false,
      // default report format
      reportFormat: 'pdf',
    };
  }

  componentDidMount() {
    if (this.props.modal === 'workforce-report') {
      this.printAreaPayload();
    }
  }

  componentDidUpdate(prevProps) {
    // If properties change from null to a value, or if property prop changes, update the state
    const newProperty = this.getPropertyFromProps(this.props);
    const prevProperty = this.getPropertyFromProps(prevProps);

    // Only update if we get a new valid property
    if ((!prevProperty && newProperty) ||
        (prevProperty && newProperty && prevProperty._id !== newProperty._id)) {
      let newAddr1 = newProperty ? (newProperty.name || '') : '';
      let newAddr2 = newProperty ? this.getPropertyAddress(newProperty) : '';

      if (newProperty && newProperty.areaType) {
        if (newProperty.areaType === 'counties') {
          const stateCd = newProperty.state_cd || newProperty.state || '';
          newAddr1 = `${newProperty.name} County, ${stateCd}`;
        } else if (newProperty.areaType === 'communities') {
          const stateCd = newProperty.state_cd || newProperty.state || '';
          newAddr1 = `${newProperty.name}, ${stateCd}`;
        }
        newAddr2 = '';
      }

      this.setState({
        addressLine1: newAddr1,
        addressLine2: newAddr2,
      });
    }

    // When modal becomes active, print area payload (if applicable)
    if (prevProps.modal !== 'workforce-report' && this.props.modal === 'workforce-report') {
      this.printAreaPayload();
    }
  }

  // Helper to get the current property from props
  getPropertyFromProps = props => {
    if (props.property) {
      return props.property;
    }

    if (props.properties && props.properties.length > 0) {
      return props.properties[0];
    }

    return null;
  }

  getPropertyAddress = property => {
    if (!property) return '';

    // Handling different property structures
    const city = property.city || (property.basicInfo && property.basicInfo.location && property.basicInfo.location.city) || '';
    const state = property.state || (property.basicInfo && property.basicInfo.location && property.basicInfo.location.state) || '';
    const street = property.street || (property.basicInfo && property.basicInfo.location && property.basicInfo.location.street) || '';

    // Return combined address
    return street ? `${street}, ${city}${city && state ? ', ' : ''}${state}` : `${city}${city && state ? ', ' : ''}${state}`;
  }

  getPropertyCoordinates = property => {
    if (!property) {
      //
      return null;
    }

    // Track all the places we've looked for coordinates
    const attempts = [];

    // Common structure: property.coordinates as [lng, lat]
    if (property.coordinates && Array.isArray(property.coordinates) && property.coordinates.length >= 2) {
      attempts.push({ source: 'property.coordinates', value: property.coordinates });
      return {
        lat: property.coordinates[1],
        lng: property.coordinates[0],
      };
    }

    // Check for nested geoLocation structure
    if (property.basicInfo && property.basicInfo.location &&
        property.basicInfo.location.geoLocation &&
        property.basicInfo.location.geoLocation.coordinates &&
        Array.isArray(property.basicInfo.location.geoLocation.coordinates) &&
        property.basicInfo.location.geoLocation.coordinates.length >= 2) {
      const coords = property.basicInfo.location.geoLocation.coordinates;
      attempts.push({ source: 'property.basicInfo.location.geoLocation.coordinates', value: coords });
      return {
        lat: coords[1],
        lng: coords[0],
      };
    }

    // Try direct latitude/longitude properties
    if (property.latitude !== undefined && property.longitude !== undefined) {
      attempts.push({ source: 'property.latitude/longitude', value: [property.longitude, property.latitude] });
      return {
        lat: parseFloat(property.latitude),
        lng: parseFloat(property.longitude),
      };
    }

    // Try lat/lng format
    if (property.lat !== undefined && property.lng !== undefined) {
      attempts.push({ source: 'property.lat/lng', value: [property.lng, property.lat] });
      return {
        lat: parseFloat(property.lat),
        lng: parseFloat(property.lng),
      };
    }

    // Try location object
    if (property.location) {
      if (property.location.coordinates && Array.isArray(property.location.coordinates) && property.location.coordinates.length >= 2) {
        attempts.push({ source: 'property.location.coordinates', value: property.location.coordinates });
        return {
          lat: property.location.coordinates[1],
          lng: property.location.coordinates[0],
        };
      }

      if (property.location.latitude !== undefined && property.location.longitude !== undefined) {
        attempts.push({ source: 'property.location.latitude/longitude', value: [property.location.longitude, property.location.latitude] });
        return {
          lat: parseFloat(property.location.latitude),
          lng: parseFloat(property.location.longitude),
        };
      }

      if (property.location.lat !== undefined && property.location.lng !== undefined) {
        attempts.push({ source: 'property.location.lat/lng', value: [property.location.lng, property.location.lat] });
        return {
          lat: parseFloat(property.location.lat),
          lng: parseFloat(property.location.lng),
        };
      }
    }

    // Try geometry for GeoJSON format
    if (property.geometry && property.geometry.coordinates &&
        Array.isArray(property.geometry.coordinates) &&
        property.geometry.coordinates.length >= 2) {
      attempts.push({ source: 'property.geometry.coordinates', value: property.geometry.coordinates });
      return {
        lng: property.geometry.coordinates[0],
        lat: property.geometry.coordinates[1],
      };
    }

    // If we got here, no coordinates were found
    //
    return null;
  }

  // Helper function to recursively search for coordinates in an object
  findCoordinatesRecursively = (obj, path = '') => {
    if (!obj || typeof obj !== 'object') return null;

    // Look for common coordinate property names
    const coordinateKeys = ['coordinates', 'geoLocation', 'location', 'position', 'geom', 'geometry', 'point'];

    for (const key of coordinateKeys) {
      if (obj[key]) {
        // If the property is an array that might be coordinates
        if (Array.isArray(obj[key]) && obj[key].length >= 2 &&
            typeof obj[key][0] === 'number' &&
            typeof obj[key][1] === 'number') {
          //
          return {
            lng: obj[key][0],
            lat: obj[key][1],
          };
        }

        // Recursively check this object
        const result = this.findCoordinatesRecursively(obj[key], `${path}.${key}`);
        if (result) return result;
      }
    }

    // Check every key in the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && typeof obj[key] === 'object' && obj[key] !== null) {
        const result = this.findCoordinatesRecursively(obj[key], `${path}.${key}`);
        if (result) return result;
      }
    }

    return null;
  }

  handleDriveTypeChange = e => {
    this.setState({ driveType: e.target.value });
  }

  handleDriveValueChange = (index, value) => {
    const maxVal = 90;
    const newDriveValues = [...this.state.driveValues];

    if (value === '') {
      newDriveValues[index] = '';
    } else {
      const numeric = parseInt(value, 10);
      if (Number.isNaN(numeric)) {
        newDriveValues[index] = '';
      } else {
        newDriveValues[index] = Math.min(maxVal, Math.max(1, numeric));
      }
    }

    this.setState({ driveValues: newDriveValues });
  }

  // Handle change for report format (pdf or xlsx)
  handleFormatChange = e => {
    this.setState({ reportFormat: e.target.value });
  }

  handleAddressChange = (field, value) => {
    this.setState({ [field]: value });
  }

  handleGenerateReport = () => {
    const property = this.getPropertyFromProps(this.props);
    const { reportFormat } = this.state;

    if (!property) {
      console.error('No property available in props:', this.props);
      alert('Cannot generate report: property data not available.');
      return;
    }

    const isArea = property && property.areaType;

    // Show loading state
    this.setState({ isGeneratingReport: true });

    let payload;
    let fileName;
    let endpoint;

    if (isArea) {
      // Determine display title
      let areaTitle = property.name || '';
      if (property.areaType === 'counties') {
        const stateCd = property.state_cd || property.state || '';
        areaTitle = `${property.name} County, ${stateCd}`;
      } else if (property.areaType === 'communities') {
        const stateCd = property.state_cd || property.state || '';
        areaTitle = `${property.name}, ${stateCd}`;
      }

      const isRegion = property.areaType === 'regions';
      const areaIdValue = isRegion ? (property.county_ids || '') : (property.id || property.intid || '');

      let subtitleValue = '';
      if (isRegion) {
        if (property.counties && property.counties.length) {
          subtitleValue = property.counties.map(cty => {
            const countyName = (cty.basicInfo && cty.basicInfo.about && cty.basicInfo.about.name) || cty.name || '';
            const st = (cty.basicInfo && cty.basicInfo.about && cty.basicInfo.about.state) || cty.state || '';
            return `${countyName}(${st})`;
          }).join(', ');
        } else {
          // fallback to state abbreviation if county list not present
          subtitleValue = property.state_cd || property.state || '';
        }
      }

      payload = {
        location: {
          title: areaTitle,
          subtitle: subtitleValue,
          id: areaIdValue,
          areaType: property.areaType,
        },
        logoUrl: this.props.logoUrl || '',
      };

      endpoint = REPORT_API_ENDPOINTS.FEATURE_WORKFORCE_REPORT;

      fileName = `Workforce_Report_${property.areaType}_${(property.id || property.intid || '').toString()}.pdf`;
    } else {
      // Existing property/building payload
      const coordinates = this.getPropertyCoordinates(property);

      if (!coordinates) {
        console.error('Failed to find coordinates. Property structure:', JSON.stringify(property, null, 2));
        alert('Property coordinates not available. Cannot generate report.');
        this.setState({ isGeneratingReport: false });
        return;
      }

      const { driveType, driveValues, addressLine1, addressLine2 } = this.state;

      payload = {
        location: {
          lat: coordinates.lat,
          lng: coordinates.lng,
          title: addressLine1 || 'Selected Property',
          subtitle: addressLine2 || 'Map Viewer Selection',
        },
        driveParams: {
          type: driveType,
          vals: (() => {
            const maxVal = 90;
            const sanitized = driveValues
              .map(v => parseInt(v, 10))
              .filter(n => !Number.isNaN(n) && n >= 1 && n <= maxVal);

            return Array.from(new Set(sanitized)).sort((a, b) => a - b);
          })(),
        },
        logoUrl: this.props.logoUrl || '',
      };

      endpoint = REPORT_API_ENDPOINTS.PIN_WORKFORCE_REPORT;

      fileName = `Workforce_Report_${coordinates.lat.toFixed(4)}_${coordinates.lng.toFixed(4)}.pdf`;
    }

    // append desired format to endpoint
    const fetchUrl = `${endpoint}?format=${reportFormat}`;

    // adjust file name extension based on selected format
    fileName = fileName.replace('.pdf', `.${reportFormat}`);

    fetch(fetchUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return response.blob();
    })
    .then(blob => {
      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a download link
      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;

      // Set the filename and trigger download
      downloadLink.download = fileName;

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Clean up the blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);

      // Reset loading state
      this.setState({ isGeneratingReport: false });

      // Close the modal after successful download
      this.props.onModalClick();
    })
    .catch(error => {
      console.error('Error fetching report:', error);
      alert('Failed to generate report. Please try again later.');
      this.setState({ isGeneratingReport: false });
    });
  }

  // Print payload for area-based workforce report
  printAreaPayload = () => {
    const area = this.getPropertyFromProps(this.props);
    if (area && area.areaType) {
      let areaTitle = area.name || '';
      if (area.areaType === 'counties') {
        const stateCd = area.state_cd || area.state || '';
        areaTitle = `${area.name} County, ${stateCd}`;
      } else if (area.areaType === 'communities') {
        const stateCd = area.state_cd || area.state || '';
        areaTitle = `${area.name}, ${stateCd}`;
      }

      const isRegion = area.areaType === 'regions';
      const areaIdValue = isRegion ? (area.county_ids || '') : (area.id || area.intid || '');

      let subtitleValue = '';
      if (isRegion) {
        if (area.counties && area.counties.length) {
          subtitleValue = area.counties.map(cty => {
            const countyName = (cty.basicInfo && cty.basicInfo.about && cty.basicInfo.about.name) || cty.name || '';
            const st = (cty.basicInfo && cty.basicInfo.about && cty.basicInfo.about.state) || cty.state || '';
            return `${countyName}(${st})`;
          }).join(', ');
        } else {
          subtitleValue = area.state_cd || area.state || '';
        }
      }

      const payload = {
        location: {
          title: areaTitle,
          subtitle: subtitleValue,
          id: areaIdValue,
          areaType: area.areaType,
        },
      };
      // eslint-disable-next-line no-console
      console.log(JSON.stringify(payload, null, 2));
      console.log('FULL AREA  : ', area);
    }
  }

  render() {
    // If admin setting disables workforce reports, do not render anything
    if (!this.props.showWorkforceReports) {
      return null;
    }

    const { modal, onModalClick } = this.props;
    const { isGeneratingReport, driveType, driveValues, addressLine1, addressLine2, reportFormat } = this.state;

    // Get current property from props
    const property = this.getPropertyFromProps(this.props);

    const isArea = property && property.areaType; // true when modal was opened from an Area

    // Only show when the modal type matches
    if (modal !== 'workforce-report') {
      return null;
    }

    // Get coordinates for displaying in the UI
    const coordinates = property ? this.getPropertyCoordinates(property) : null;
    const propertyName = property ? (property.name || '') : '';

    // Common styles
    const inputStyle = {
      padding: '8px',
      margin: '5px 0',
      borderRadius: '3px',
      border: '1px solid #ccc',
      width: '100%',
    };

    const labelStyle = {
      fontWeight: 'bold',
      marginTop: '10px',
      display: 'block',
    };

    const numberInputStyle = {
      ...inputStyle,
      width: '70px',
    };

    return (
      <div className="modal">
        <div className="modal-wrapper building-modal">
          <ViewerPrimaryColorUserStyle domStyle="backgroundColor">
            <div className="modal-header">
              <h2 className="modal-title">Workforce Reports</h2>
              <a tabIndex="0" onClick={onModalClick}>
                <div className="modal-close">
                  {svgInline(closeIcon)}
                </div>
              </a>
            </div>
          </ViewerPrimaryColorUserStyle>
          <div className="modal-body workforce-modal-form">
            <h2>{propertyName}</h2>

            {!property && (
              <div style={{ color: 'red', padding: '15px', backgroundColor: '#ffeeee', margin: '10px 0', borderRadius: '4px' }}>
                <p><strong>Warning:</strong> Property data not available.</p>
                <p>Please close this modal and try again.</p>
              </div>
            )}

            {/* Location Information Display */}
            {coordinates && (
              <div
                className="selected-location-info" style={{
                  margin: '15px 0',
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  border: '1px solid #e9ecef',
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Property Location:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ minWidth: '140px' }}>
                    <span style={{ color: '#666' }}>Latitude:</span> <span style={{ fontWeight: '500' }}>{coordinates.lat.toFixed(6)}</span>
                  </div>
                  <div style={{ minWidth: '140px' }}>
                    <span style={{ color: '#666' }}>Longitude:</span> <span style={{ fontWeight: '500' }}>{coordinates.lng.toFixed(6)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Report Options Form */}
            <div className="report-options" style={{ marginTop: '20px' }}>

              {/* Address Display - Read-only for Line 1 */}
              <div className="address-inputs">
                <label style={labelStyle} htmlFor="addressLine1">{isArea ? 'Area Name:' : 'Property Name:'}</label>
                <input
                  id="addressLine1"
                  type="text"
                  style={{ ...inputStyle, background: '#f5f5f5' }}
                  value={addressLine1}
                  readOnly
                />

                {!isArea && (
                  <label style={labelStyle} htmlFor="addressLine2">Subtitle:</label>
                )}
                {!isArea && (
                  <input
                    id="addressLine2"
                    type="text"
                    style={inputStyle}
                    value={addressLine2}
                    onChange={e => this.handleAddressChange('addressLine2', e.target.value)}
                    placeholder="Additional address information"
                  />
                )}
              </div>

              {/* Drive Type Selection - hide in area mode */}
              {!isArea && (
                <div className="drive-type" style={{ marginTop: '15px' }}>
                  <label style={labelStyle}>Analysis Boundary Type:</label>
                  <div style={{ display: 'flex', gap: '15px', margin: '10px 0' }}>
                    <label>
                      <input
                        type="radio"
                        name="driveType"
                        value="mins"
                        checked={driveType === 'mins'}
                        onChange={this.handleDriveTypeChange}
                      />
                      {' '}Drive Time (minutes)
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="driveType"
                        value="miles"
                        checked={driveType === 'miles'}
                        onChange={this.handleDriveTypeChange}
                      />
                      {' '}Drive Distance (miles)
                    </label>
                  </div>
                </div>
              )}

              {/* Drive Values - hide in area mode */}
              {!isArea && (
                <div className="drive-values">
                  <label style={labelStyle}>
                    {driveType === 'mins' ? 'Drive Times (1-90 minutes):' : 'Drive Distances (1-90 miles):'}
                  </label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '10px 0' }}>
                    {driveValues.map((value, index) => (
                      <input
                        key={index}
                        type="number"
                        min="1"
                        max="90"
                        style={numberInputStyle}
                        value={value}
                        onChange={e => this.handleDriveValueChange(index, e.target.value)}
                      />
                    ))}
                  </div>
                  <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                    Enter up to 3 values between 1 and {driveType === 'mins' ? '90 minutes' : '90 miles'}.
                  </p>
                </div>
              )}

              {/* Report Format Selection */}
              <div className="report-format" style={{ marginTop: '15px' }}>
                <label style={labelStyle}>Report Format:</label>
                <div style={{ display: 'flex', gap: '15px', margin: '10px 0' }}>
                  <label>
                    <input
                      type="radio"
                      name="reportFormat"
                      value="pdf"
                      checked={reportFormat === 'pdf'}
                      onChange={this.handleFormatChange}
                    />
                    {' '}PDF
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="reportFormat"
                      value="xlsx"
                      checked={reportFormat === 'xlsx'}
                      onChange={this.handleFormatChange}
                    />
                    {' '}Excel
                  </label>
                </div>
              </div>
            </div>

            {/* Generate Report Button */}
            <ViewerPrimaryColorUserStyle domStyle="backgroundColor">
              <button
                className="btn workforce-generate-report-button"
                onClick={this.handleGenerateReport}
                disabled={isGeneratingReport || !property}
                style={{
                  padding: '10px 15px',
                  cursor: (isGeneratingReport || !property) ? 'not-allowed' : 'pointer',
                  opacity: (isGeneratingReport || !property) ? 0.7 : 1,
                  border: 'none',
                  borderRadius: '3px',
                  fontWeight: 'bold',
                  color: 'white',
                  display: 'block',
                  width: '100%',
                  marginTop: '20px',
                  textAlign: 'center',
                }}
              >
                {isGeneratingReport ? 'Generating Report...' : 'Generate Workforce Report'}
              </button>
            </ViewerPrimaryColorUserStyle>
          </div>
        </div>
        <div className="modal-closer" onClick={onModalClick} />
      </div>
    );
  }
}

WorkforceReportsModalComponent.propTypes = {
  onModalClick: PropTypes.func,
  modal: PropTypes.string,
  properties: PropTypes.array,
  property: PropTypes.object, // Direct property prop
  showWorkforceReports: PropTypes.bool,
  logoUrl: PropTypes.string,
};

function mapStateToProps(state) {
  return {
    modal: state.modal,
    properties: state.modalProperties,
    showWorkforceReports: !!(state.viewer && state.viewer.adminSettings && state.viewer.adminSettings.lightcastWorkforceData),
    logoUrl: state.viewer && state.viewer.logo ? `${storage.cloudStoragePath}/${state.viewer.logo}` : '',
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onModalClick: () => { dispatch(deactivateModal()); },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(WorkforceReportsModalComponent);



// WEBPACK FOOTER //
// ./src/components/WorkforceReportsModal/WorkforceReportsModal.js